import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
  useTheme as muiUseTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import SportsBaseballIcon from '@mui/icons-material/SportsBaseball';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SportsFootballIcon from '@mui/icons-material/SportsFootball';
import InsightsIcon from '@mui/icons-material/Insights';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FantasySportsLogo } from './FantasySportsLogo';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

export const SIDEBAR_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

const NEON_COLORS = {
  blue: '#1976d2',
  orange: '#ff6b35',
  green: '#4caf50',
  purple: '#ab47bc',
};

interface NavLink { type: 'link'; name: string; icon: React.ReactElement; path: string; color: string; exact?: boolean; permission?: string }
interface NavSection { type: 'section'; label: string; permission?: string }
interface NavDivider { type: 'divider' }
type NavItem = NavLink | NavSection | NavDivider;

// `permission` gates a link or section header. Links without a `permission` are
// always visible (e.g. playbook docs). buildVisibleNav() below filters the static
// list by the user's JWT perm claims and collapses dividers/sections that end up
// with no visible children.
const mainNavItems: NavItem[] = [
  { type: 'section', label: 'BETTING', permission: 'Betting.View' },
  { type: 'link', name: 'Baseball', icon: <SportsBaseballIcon />, path: '/baseball/betting', color: NEON_COLORS.blue, permission: 'Betting.View' },
  { type: 'link', name: 'Basketball', icon: <SportsBasketballIcon />, path: '/basketball/betting', color: NEON_COLORS.orange, permission: 'Betting.View' },
  { type: 'link', name: 'Football', icon: <SportsFootballIcon />, path: '/football/betting', color: NEON_COLORS.green, permission: 'Betting.View' },
  // Betting Analytics is the props/parlay RETROSPECTIVE (results/tuning/backtesting) —
  // bottom of the BETTING group, separated from the live sport links by a divider so the
  // live-vs-retrospective split reads at a glance (Contest History mirrors it under DFS).
  { type: 'divider' },
  { type: 'link', name: 'Betting Analytics', icon: <InsightsIcon />, path: '/analytics', color: NEON_COLORS.purple, permission: 'Analytics.View' },
  { type: 'divider' },
  { type: 'section', label: 'DFS LINEUPS', permission: 'DFS.View' },
  { type: 'link', name: 'Baseball', icon: <SportsBaseballIcon />, path: '/baseball/dfs', color: NEON_COLORS.blue, permission: 'DFS.View' },
  { type: 'link', name: 'Basketball', icon: <SportsBasketballIcon />, path: '/basketball/dfs', color: NEON_COLORS.orange, permission: 'DFS.View' },
  { type: 'link', name: 'Football', icon: <SportsFootballIcon />, path: '/football/dfs', color: NEON_COLORS.green, permission: 'DFS.View' },
  // Contest History is the DFS RETROSPECTIVE (real-money P&L + projection accuracy) —
  // divider separates it from the live DFS sport links, mirroring Betting Analytics above.
  { type: 'divider' },
  { type: 'link', name: 'Contest History', icon: <ReceiptLongIcon />, path: '/contests/history', color: NEON_COLORS.purple, permission: 'Contests.History.View' },
  { type: 'divider' },
  { type: 'section', label: 'PLAYBOOK' },
  { type: 'link', name: 'Baseball', icon: <SportsBaseballIcon />, path: '/playbook/baseball', color: NEON_COLORS.blue },
  { type: 'link', name: 'Basketball', icon: <SportsBasketballIcon />, path: '/playbook/basketball', color: NEON_COLORS.orange },
  { type: 'link', name: 'Football', icon: <SportsFootballIcon />, path: '/playbook/football', color: NEON_COLORS.green },
];

// Admin-area nav. Rendered instead of mainNavItems when the URL is under /admin.
// Users/Roles/AuditLog/Health gate on their specific read permissions; Overview
// is always visible inside the admin area so a user with any admin perm has a
// landing page.
const adminNavItems: NavItem[] = [
  { type: 'link', name: 'Overview', icon: <DashboardIcon />, path: '/admin', color: NEON_COLORS.purple, exact: true },
  { type: 'divider' },
  { type: 'link', name: 'Users', icon: <PeopleIcon />, path: '/admin/users', color: NEON_COLORS.purple, permission: 'Admin.Users.Read' },
  { type: 'link', name: 'Roles', icon: <VerifiedUserIcon />, path: '/admin/roles', color: NEON_COLORS.purple, permission: 'Admin.Roles.Read' },
  { type: 'link', name: 'Audit Log', icon: <HistoryIcon />, path: '/admin/audit-log', color: NEON_COLORS.purple, permission: 'Admin.AuditLog.Read' },
  { type: 'link', name: 'System Health', icon: <MonitorHeartIcon />, path: '/admin/health', color: NEON_COLORS.purple, permission: 'Admin.Health.Read' },
];

const ADMIN_PERMISSIONS = ['Admin.Users.Read', 'Admin.Roles.Read', 'Admin.AuditLog.Read', 'Admin.Health.Read'];

// Derive a short, screen-reader-friendly title for the mobile AppBar so users
// know where they are without opening the drawer. Falls back to "Fantasy Sports".
function getPageTitle(pathname: string): string {
  if (pathname === '/') return 'Dashboard';
  if (pathname === '/analytics') return 'Betting Analytics';
  if (pathname === '/settings/security') return 'Security';
  if (pathname === '/admin') return 'Admin';
  if (pathname.startsWith('/admin/users')) return 'Users';
  if (pathname.startsWith('/admin/roles')) return 'Roles';
  if (pathname.startsWith('/admin/audit-log')) return 'Audit Log';
  if (pathname.startsWith('/admin/health')) return 'System Health';
  if (pathname.startsWith('/baseball/lineups')) return 'Baseball Lineups';
  if (pathname.startsWith('/basketball/lineups')) return 'Basketball Lineups';
  if (pathname.startsWith('/football/lineups')) return 'Football Lineups';
  if (pathname.startsWith('/contests/history')) return 'Contest History';
  if (pathname.startsWith('/baseball/dfs')) return 'Baseball DFS';
  if (pathname.startsWith('/basketball/dfs')) return 'Basketball DFS';
  if (pathname.startsWith('/football/dfs')) return 'Football DFS';
  if (pathname.startsWith('/baseball')) return 'Baseball';
  if (pathname.startsWith('/basketball')) return 'Basketball';
  if (pathname.startsWith('/football')) return 'Football';
  if (pathname.startsWith('/playbook/baseball')) return 'Baseball Playbook';
  if (pathname.startsWith('/playbook/basketball')) return 'Basketball Playbook';
  if (pathname.startsWith('/playbook/football')) return 'Football Playbook';
  return 'Fantasy Sports';
}

// Drop items the user has no permission for, then prune orphan section headers
// (no following links) and duplicate/leading/trailing dividers.
function buildVisibleNav(source: NavItem[], permissions: string[]): NavItem[] {
  const has = (p?: string) => !p || permissions.includes(p);

  const filtered = source.filter(item => {
    if (item.type === 'link') return has(item.permission);
    if (item.type === 'section') return has(item.permission);
    return true;
  });

  // Walk forward: drop a section header if no 'link' follows before next section/divider
  const withLiveSections: NavItem[] = [];
  for (let i = 0; i < filtered.length; i++) {
    const item = filtered[i];
    if (item.type === 'section') {
      let hasChild = false;
      for (let j = i + 1; j < filtered.length; j++) {
        const next = filtered[j];
        if (next.type === 'section' || next.type === 'divider') break;
        if (next.type === 'link') { hasChild = true; break; }
      }
      if (!hasChild) continue;
    }
    withLiveSections.push(item);
  }

  // Collapse adjacent/leading/trailing dividers
  const result: NavItem[] = [];
  for (const item of withLiveSections) {
    if (item.type === 'divider') {
      if (result.length === 0) continue;
      if (result[result.length - 1].type === 'divider') continue;
    }
    result.push(item);
  }
  while (result.length && result[result.length - 1].type === 'divider') result.pop();

  return result;
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = muiUseTheme();
  const { isDarkMode, toggleTheme } = useTheme();
  const { email, permissions, logout } = useAuth();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // GitLab-style context swap: when the URL is under /admin the sidebar renders
  // admin-specific nav instead of the sport/DFS/betting list. Toggled only by
  // location so the two surfaces never mix.
  const isAdminContext = location.pathname.startsWith('/admin');
  const navSource = isAdminContext ? adminNavItems : mainNavItems;
  const visibleNav = React.useMemo(() => buildVisibleNav(navSource, permissions), [navSource, permissions]);

  // Any admin perm → main sidebar shows "Admin →" entry at the bottom as the
  // single entry point into the admin context.
  const hasAnyAdminPermission = ADMIN_PERMISSIONS.some(p => permissions.includes(p));

  const [collapsed, setCollapsed] = React.useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const currentWidth = collapsed && !isMobile ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  // Theme-aware colors
  const sidebarBg = muiTheme.palette.background.default;
  const borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';
  const textDefault = muiTheme.palette.text.secondary;
  const textActive = muiTheme.palette.text.primary;
  const sectionLabel = muiTheme.palette.text.disabled;

  const handleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(next));
  };

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    // Also match lineup sub-routes: /baseball/dfs should highlight when on /baseball/lineups/:id
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNav = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const sidebarContent = (showCollapsed: boolean) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: sidebarBg,
        overflow: 'hidden',
      }}
    >
      {/* Logo */}
      <Box
        onClick={() => handleNav('/')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: showCollapsed ? 0 : 2.5,
          py: 2.5,
          cursor: 'pointer',
          justifyContent: showCollapsed ? 'center' : 'flex-start',
          '&:hover .app-title': {
            color: muiTheme.palette.primary.main,
            textShadow: isDarkMode ? '0 0 20px rgba(25, 118, 210, 0.8)' : 'none',
          },
        }}
      >
        <FantasySportsLogo width={28} height={28} onDark={isDarkMode} />
        {!showCollapsed && (
          <Typography
            className="app-title"
            variant="subtitle1"
            sx={{
              color: textActive,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: '0.85rem',
              textShadow: isDarkMode ? '0 0 10px rgba(25, 118, 210, 0.4)' : 'none',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap',
            }}
          >
            Fantasy Sports
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor, mx: showCollapsed ? 1 : 2 }} />

      {/* Nav items. `minHeight: 0` lets this flex child shrink below its
          content height so `overflowY: auto` actually engages — without it
          flex children stay at content size and the scroll never appears,
          which clips admin items below the fold on short phones. */}
      <List sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: showCollapsed ? 0.5 : 1, py: 1 }}>
        {visibleNav.map((item, i) => {
          if (item.type === 'divider') {
            return <Divider key={`d-${i}`} sx={{ borderColor, mx: showCollapsed ? 0.5 : 1, my: 0.5 }} />;
          }

          if (item.type === 'section') {
            if (showCollapsed) return null;
            return (
              <Typography
                key={item.label}
                variant="caption"
                sx={{
                  color: sectionLabel,
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  fontSize: '0.65rem',
                  px: 2,
                  pt: 1.5,
                  pb: 0.5,
                  display: 'block',
                }}
              >
                {item.label}
              </Typography>
            );
          }

          const active = isActive(item.path, item.exact);

          const button = (
            <ListItemButton
              key={`${item.path}-${item.name}`}
              onClick={() => handleNav(item.path)}
              sx={{
                borderRadius: 1.5,
                mb: 0.3,
                px: showCollapsed ? 0 : 2,
                py: 0.8,
                justifyContent: showCollapsed ? 'center' : 'flex-start',
                borderLeft: active ? `3px solid ${item.color}` : '3px solid transparent',
                backgroundColor: active
                  ? (isDarkMode ? `${item.color}20` : `${item.color}12`)
                  : 'transparent',
                boxShadow: active && isDarkMode
                  ? `inset 0 0 20px ${item.color}10, 0 0 8px ${item.color}08`
                  : 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: active
                    ? (isDarkMode ? `${item.color}28` : `${item.color}18`)
                    : (isDarkMode ? `${item.color}12` : `${item.color}08`),
                  boxShadow: isDarkMode ? `0 0 12px ${item.color}15` : 'none',
                  '& .nav-icon': {
                    color: item.color,
                    filter: isDarkMode ? `drop-shadow(0 0 4px ${item.color})` : 'none',
                  },
                  '& .nav-text': { color: textActive },
                },
              }}
            >
              <ListItemIcon
                className="nav-icon"
                sx={{
                  color: active ? item.color : textDefault,
                  minWidth: showCollapsed ? 0 : 36,
                  justifyContent: 'center',
                  filter: active && isDarkMode ? `drop-shadow(0 0 6px ${item.color})` : 'none',
                  transition: 'all 0.2s ease',
                  '& .MuiSvgIcon-root': { fontSize: '1.2rem' },
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!showCollapsed && (
                <ListItemText
                  className="nav-text"
                  primary={item.name}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: active ? textActive : textDefault,
                      fontWeight: active ? 600 : 400,
                      fontSize: '0.84rem',
                      textShadow: active && isDarkMode ? `0 0 8px ${item.color}40` : 'none',
                      transition: 'all 0.2s ease',
                    },
                  }}
                />
              )}
            </ListItemButton>
          );

          if (showCollapsed) {
            return (
              <Tooltip key={`${item.path}-${item.name}`} title={item.name} placement="right" arrow>
                {button}
              </Tooltip>
            );
          }
          return button;
        })}
      </List>

      {/* Bottom: user + admin + theme + collapse */}
      <Box sx={{ px: showCollapsed ? 0.5 : 1, pb: 2 }}>
        <Divider sx={{ borderColor, mx: showCollapsed ? 0.5 : 1, mb: 1 }} />

        {/* User email */}
        {!showCollapsed && email && (
          <Typography
            variant="caption"
            sx={{
              color: sectionLabel,
              fontSize: '0.65rem',
              px: 2,
              pt: 0.5,
              pb: 0.5,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {email}
          </Typography>
        )}

        {/* GitLab-style context entry point: in main app, show a single "Admin →"
            link (if user has any Admin.* permission). Inside /admin/*, show
            "← Back to app" in the same slot. */}
        {!isAdminContext && hasAnyAdminPermission && (
          <Tooltip title="Admin" placement="right" arrow disableHoverListener={!showCollapsed}>
            <ListItemButton
              onClick={() => handleNav('/admin')}
              sx={{
                borderRadius: 1.5,
                mb: 0.3,
                px: showCollapsed ? 0 : 2,
                py: 0.8,
                justifyContent: showCollapsed ? 'center' : 'flex-start',
                '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              <ListItemIcon sx={{ color: textDefault, minWidth: showCollapsed ? 0 : 36, justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: '1.2rem' } }}>
                <AdminPanelSettingsIcon />
              </ListItemIcon>
              {!showCollapsed && (
                <ListItemText primary="Admin" sx={{ '& .MuiListItemText-primary': { color: textDefault, fontSize: '0.84rem' } }} />
              )}
            </ListItemButton>
          </Tooltip>
        )}

        {isAdminContext && (
          <Tooltip title="Back to app" placement="right" arrow disableHoverListener={!showCollapsed}>
            <ListItemButton
              onClick={() => handleNav('/')}
              sx={{
                borderRadius: 1.5,
                mb: 0.3,
                px: showCollapsed ? 0 : 2,
                py: 0.8,
                justifyContent: showCollapsed ? 'center' : 'flex-start',
                '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              <ListItemIcon sx={{ color: textDefault, minWidth: showCollapsed ? 0 : 36, justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: '1.2rem' } }}>
                <ArrowBackIcon />
              </ListItemIcon>
              {!showCollapsed && (
                <ListItemText primary="Back to app" sx={{ '& .MuiListItemText-primary': { color: textDefault, fontSize: '0.84rem' } }} />
              )}
            </ListItemButton>
          </Tooltip>
        )}

        {/* Security Settings */}
        <Tooltip title="Security" placement="right" arrow disableHoverListener={!showCollapsed}>
          <ListItemButton
            onClick={() => handleNav('/settings/security')}
            sx={{
              borderRadius: 1.5,
              mb: 0.3,
              px: showCollapsed ? 0 : 2,
              py: 0.8,
              justifyContent: showCollapsed ? 'center' : 'flex-start',
              '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
            }}
          >
            <ListItemIcon sx={{ color: textDefault, minWidth: showCollapsed ? 0 : 36, justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: '1.2rem' } }}>
              <SecurityIcon />
            </ListItemIcon>
            {!showCollapsed && (
              <ListItemText primary="Security" sx={{ '& .MuiListItemText-primary': { color: textDefault, fontSize: '0.84rem' } }} />
            )}
          </ListItemButton>
        </Tooltip>

        {/* Logout */}
        <Tooltip title="Sign Out" placement="right" arrow disableHoverListener={!showCollapsed}>
          <ListItemButton
            onClick={async () => { await logout(); navigate('/login'); }}
            sx={{
              borderRadius: 1.5,
              mb: 0.3,
              px: showCollapsed ? 0 : 2,
              py: 0.8,
              justifyContent: showCollapsed ? 'center' : 'flex-start',
              '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
            }}
          >
            <ListItemIcon sx={{ color: textDefault, minWidth: showCollapsed ? 0 : 36, justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: '1.2rem' } }}>
              <LogoutIcon />
            </ListItemIcon>
            {!showCollapsed && (
              <ListItemText primary="Sign Out" sx={{ '& .MuiListItemText-primary': { color: textDefault, fontSize: '0.84rem' } }} />
            )}
          </ListItemButton>
        </Tooltip>

        <Divider sx={{ borderColor, mx: showCollapsed ? 0.5 : 1, my: 0.5 }} />

        <Tooltip title={isDarkMode ? 'Light Mode' : 'Dark Mode'} placement="right" arrow disableHoverListener={!showCollapsed}>
          <ListItemButton
            onClick={toggleTheme}
            sx={{
              borderRadius: 1.5,
              px: showCollapsed ? 0 : 2,
              py: 0.8,
              justifyContent: showCollapsed ? 'center' : 'flex-start',
              '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
            }}
          >
            <ListItemIcon sx={{ color: textDefault, minWidth: showCollapsed ? 0 : 36, justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: '1.2rem' } }}>
              {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </ListItemIcon>
            {!showCollapsed && (
              <ListItemText primary={isDarkMode ? 'Light Mode' : 'Dark Mode'} sx={{ '& .MuiListItemText-primary': { color: textDefault, fontSize: '0.84rem' } }} />
            )}
          </ListItemButton>
        </Tooltip>

        {!isMobile && (
          <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement="right" arrow disableHoverListener={!showCollapsed}>
            <ListItemButton
              onClick={handleCollapse}
              sx={{
                borderRadius: 1.5,
                mt: 0.3,
                px: showCollapsed ? 0 : 2,
                py: 0.8,
                justifyContent: showCollapsed ? 'center' : 'flex-start',
                '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              <ListItemIcon sx={{ color: textDefault, minWidth: showCollapsed ? 0 : 36, justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: '1.2rem' } }}>
                {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </ListItemIcon>
              {!showCollapsed && (
                <ListItemText primary="Collapse" sx={{ '& .MuiListItemText-primary': { color: textDefault, fontSize: '0.84rem' } }} />
              )}
            </ListItemButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    const pageTitle = getPageTitle(location.pathname);
    return (
      <>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: sidebarBg,
            borderBottom: `1px solid ${borderColor}`,
            // iOS notch padding so the toolbar isn't hidden under the status bar
            pt: 'env(safe-area-inset-top)',
          }}
        >
          <Toolbar sx={{ minHeight: 56, px: 2, gap: 1 }}>
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ color: textDefault }}>
              <MenuIcon />
            </IconButton>
            <Box
              onClick={() => handleNav('/')}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flexShrink: 0 }}
            >
              <FantasySportsLogo width={24} height={24} onDark={isDarkMode} />
            </Box>
            <Typography
              noWrap
              sx={{
                color: textActive,
                fontWeight: 600,
                fontSize: '0.95rem',
                flexGrow: 1,
                ml: 0.5,
                minWidth: 0,
              }}
            >
              {pageTitle}
            </Typography>
            <IconButton onClick={toggleTheme} sx={{ color: textDefault }} aria-label="Toggle theme">
              {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Toolbar>
        </AppBar>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              backgroundColor: sidebarBg,
              borderRight: `1px solid ${borderColor}`,
              pt: 'env(safe-area-inset-top)',
              pb: 'env(safe-area-inset-bottom)',
            },
          }}
        >
          {sidebarContent(false)}
        </Drawer>
      </>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open
      sx={{
        width: currentWidth,
        flexShrink: 0,
        transition: 'width 0.2s ease',
        '& .MuiDrawer-paper': {
          width: currentWidth,
          backgroundColor: sidebarBg,
          borderRight: `1px solid ${borderColor}`,
          transition: 'width 0.2s ease',
          overflowX: 'hidden',
        },
      }}
    >
      {sidebarContent(collapsed)}
    </Drawer>
  );
};

export default Sidebar;
